import { formatUZS, type Dashboard, type Forecast } from "@elektr/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { useState } from "react";

import { TrendChart } from "@/components/charts/TrendChart";
import { Badge, Card, EmptyState, PageHeader, Spinner } from "@/components/ui/card";
import { Input, Select } from "@/components/ui/form";
import { api, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type Period = "today" | "week" | "month" | "custom";
type MetricKey = keyof Dashboard["metrics"];
type SeriesKey = keyof Dashboard["series"];
type ForecastMetric = Forecast["metric"];

const PERIODS = [
  ["today", "analytics.period_today"],
  ["week", "analytics.period_week"],
  ["month", "analytics.period_month"],
  ["custom", "analytics.period_custom"],
] as const;

// Kalitlar literal bo'lishi kerak: i18n qamrov testi dinamik kalitlarni ko'rmaydi
const METRICS: { key: MetricKey; label: string; money?: boolean }[] = [
  { key: "properties_new", label: "analytics.m_properties_new" },
  { key: "customers_new", label: "analytics.m_customers_new" },
  { key: "media_uploaded", label: "analytics.m_media_uploaded" },
  { key: "qr_scans", label: "analytics.m_qr_scans" },
  { key: "product_views", label: "analytics.m_product_views" },
  { key: "product_contacts", label: "analytics.m_product_contacts" },
  { key: "leads_new", label: "analytics.m_leads_new" },
  { key: "sales_count", label: "analytics.m_sales_count" },
  { key: "sales_total_uzs", label: "analytics.m_sales_total_uzs", money: true },
];
const FORECAST_OPTIONS: { key: ForecastMetric; label: string; money?: boolean }[] = [
  { key: "sales_total_uzs", label: "analytics.m_sales_total_uzs", money: true },
  { key: "sales_count", label: "analytics.m_sales_count" },
  { key: "leads_new", label: "analytics.m_leads_new" },
  { key: "qr_scans", label: "analytics.m_qr_scans" },
];
const TREND_KEYS = {
  up: "analytics.trend_up",
  down: "analytics.trend_down",
  flat: "analytics.trend_flat",
} as const;

const num = (n: number, money?: boolean) =>
  money ? formatUZS(n) : new Intl.NumberFormat("ru-RU").format(Math.round(n));

function Change({ pct }: { pct: number | null | undefined }) {
  const { t } = useT();
  if (pct === null || pct === undefined)
    return <span className="text-xs text-muted-foreground">{t("analytics.no_prev")}</span>;
  const Icon = pct > 0 ? ArrowUpRight : pct < 0 ? ArrowDownRight : Minus;
  const tone = pct > 0 ? "text-green-700" : pct < 0 ? "text-red-700" : "text-muted-foreground";
  return (
    <span
      className={cn("inline-flex items-center gap-0.5 text-xs font-semibold", tone)}
      title={t("analytics.vs_prev")}
    >
      <Icon className="size-3.5" aria-hidden />
      {pct > 0 ? "+" : ""}
      {String(pct).replace(".", ",")}%
    </span>
  );
}

/** Dashboard: davr ko'rsatkichlari (oldingi davr bilan), kunlik grafik, hududlar va prognoz. */
export function AnalyticsPage() {
  const { t } = useT();
  const [period, setPeriod] = useState<Period>("week");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [chartKey, setChartKey] = useState<SeriesKey>("properties_new");
  const [fMetric, setFMetric] = useState<ForecastMetric>("sales_total_uzs");

  const rangeInvalid = period === "custom" && !!from && !!to && from > to;
  const ready = period !== "custom" || (!!from && !!to && !rangeInvalid);

  const dash = useQuery({
    queryKey: ["analytics", "dashboard", period, from, to],
    enabled: ready,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/analytics/dashboard/", {
          params: {
            query: {
              period,
              date_from: period === "custom" ? from : undefined,
              date_to: period === "custom" ? to : undefined,
            },
          },
        }),
      ),
  });
  const fc = useQuery({
    queryKey: ["analytics", "forecast", fMetric],
    queryFn: async () =>
      unwrap(await api.GET("/api/analytics/forecast/", { params: { query: { metric: fMetric } } })),
  });

  const d = dash.data;
  const series = d?.series[chartKey] ?? [];
  const chartMetric = METRICS.find((m) => m.key === chartKey)!;
  const fOpt = FORECAST_OPTIONS.find((m) => m.key === fMetric)!;
  const hasChartData = series.some((p) => p.value > 0);

  return (
    <>
      <PageHeader title={t("analytics.title")} />
      <Card className="mb-4 flex flex-wrap items-end gap-3">
        <div role="group" aria-label={t("analytics.period")} className="flex flex-wrap gap-1.5">
          {PERIODS.map(([p, label]) => (
            <button
              key={p}
              type="button"
              aria-pressed={period === p}
              onClick={() => setPeriod(p)}
              className={cn(
                "rounded-lg border px-3 py-2 text-sm font-semibold",
                period === p
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card hover:bg-muted",
              )}
            >
              {t(label)}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <>
            <Input
              type="date"
              className="w-44"
              aria-label={t("analytics.from")}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <Input
              type="date"
              className="w-44"
              aria-label={t("analytics.to")}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </>
        )}
        {rangeInvalid && (
          <p role="alert" className="text-sm text-red-700">
            {t("analytics.bad_range")}
          </p>
        )}
      </Card>

      {dash.isError && (
        <p role="alert" className="mb-4 text-red-700">
          {t("analytics.error")}
        </p>
      )}
      {!d && dash.isLoading && <Spinner className="mx-auto my-10 size-8" />}

      {d && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="metric-cards">
            {METRICS.filter((m) => d.metrics[m.key]).map((m) => {
              const v = d.metrics[m.key]!;
              const selectable = m.key in d.series;
              const body = (
                <>
                  <div className="text-sm text-muted-foreground">{t(m.label)}</div>
                  <div className="mt-1 text-2xl font-bold">{num(v.value, m.money)}</div>
                  <Change pct={v.change_pct} />
                </>
              );
              return selectable ? (
                <button
                  key={m.key}
                  type="button"
                  aria-pressed={chartKey === m.key}
                  onClick={() => setChartKey(m.key as SeriesKey)}
                  className={cn(
                    "rounded-xl border bg-card p-4 text-left shadow-sm hover:bg-muted",
                    chartKey === m.key ? "border-primary ring-2 ring-primary/30" : "border-border",
                  )}
                >
                  {body}
                </button>
              ) : (
                <div key={m.key} className="rounded-xl border border-border bg-card p-4 shadow-sm">
                  {body}
                </div>
              );
            })}
          </div>

          <Card className="mt-4">
            <h2 className="mb-2 font-bold">
              {t("analytics.chart_title", { metric: t(chartMetric.label) })}
            </h2>
            {hasChartData ? (
              <TrendChart
                history={series.map((p) => ({ date: p.date, value: p.value }))}
                label={t("analytics.chart_label", { metric: t(chartMetric.label) })}
                format={(n) => num(n, chartMetric.money)}
              />
            ) : (
              <EmptyState>{t("analytics.chart_empty")}</EmptyState>
            )}
          </Card>

          <Card className="mt-4">
            <h2 className="mb-2 font-bold">{t("analytics.districts")}</h2>
            {d.by_district.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("analytics.districts_empty")}</p>
            ) : (
              <ul className="space-y-1.5">
                {d.by_district.map((x) => (
                  <li key={x.district} className="flex items-center gap-3 text-sm">
                    <span className="w-40 shrink-0 truncate">{x.district}</span>
                    <span
                      className="h-2.5 rounded-full bg-primary"
                      style={{ width: `${(x.count / d.by_district[0]!.count) * 60}%` }}
                      aria-hidden
                    />
                    <span className="font-semibold">{x.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}

      <Card className="mt-4" data-testid="forecast">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-bold">{t("analytics.forecast")}</h2>
          <Select
            className="w-56"
            aria-label={t("analytics.forecast_metric")}
            value={fMetric}
            onChange={(e) => setFMetric(e.target.value as ForecastMetric)}
          >
            {FORECAST_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>
                {t(o.label)}
              </option>
            ))}
          </Select>
        </div>
        {fc.isLoading && <Spinner />}
        {fc.isError && (
          <p role="alert" className="text-red-700">
            {t("analytics.error")}
          </p>
        )}
        {fc.data && !fc.data.enough_data && (
          <p className="rounded-lg bg-muted p-4 text-sm" role="status">
            {t("analytics.forecast_need", {
              needed: fc.data.weeks_needed,
              have: fc.data.weeks_have,
            })}
          </p>
        )}
        {fc.data?.enough_data && fc.data.next_month && fc.data.trend && (
          <>
            <div className="mb-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <div>
                <div className="text-sm text-muted-foreground">{t("analytics.forecast_next")}</div>
                <div className="text-2xl font-bold" data-testid="forecast-total">
                  {num(fc.data.next_month.value, fOpt.money)}
                </div>
              </div>
              <Badge
                tone={
                  fc.data.trend === "up"
                    ? "success"
                    : fc.data.trend === "down"
                      ? "danger"
                      : "neutral"
                }
              >
                {t(TREND_KEYS[fc.data.trend])}
              </Badge>
              <span className="text-sm text-muted-foreground" data-testid="forecast-range">
                {t("analytics.forecast_range", {
                  low: num(fc.data.next_month.low, fOpt.money),
                  high: num(fc.data.next_month.high, fOpt.money),
                })}
              </span>
            </div>
            <TrendChart
              history={fc.data.history.map((p) => ({ date: p.week_start, value: p.value }))}
              forecast={fc.data.forecast.map((p) => ({
                date: p.week_start,
                value: p.value,
                low: p.low,
                high: p.high,
              }))}
              label={t("analytics.chart_label", { metric: t(fOpt.label) })}
              format={(n) => num(n, fOpt.money)}
            />
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span>— {t("analytics.legend_history")}</span>
              <span>- - {t("analytics.legend_forecast")}</span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{t("analytics.forecast_note")}</p>
          </>
        )}
      </Card>
    </>
  );
}
